@use '@/styles/mastodon/mixins';

.base {
  --bg: transparent;
  --bg-hover: color-mix(in srgb, var(--bg) 90%, var(--fg));
  --fg: var(--color-text-primary);
  --inline-padding: var(--space-2);

  @include mixins.type-label;

  background-color: var(--bg);
  border-radius: var(--radius-round);
  color: var(--fg);
  transition:
    background-color 200ms,
    color 200ms,
    opacity 200ms;
  border: 0;
  appearance: none;
  cursor: pointer;
  position: relative;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding-inline: var(--inline-padding);
  box-sizing: border-box;

  &:hover:not(
      :active,
      :disabled,
      [aria-disabled='true'],
      [aria-pressed='true'],
      [aria-expanded='true']
    ) {
    background-color: var(--bg-hover);
  }

  &:focus-visible,
  &:focus-within {
    outline: var(--outline-focus-default);
    outline-offset: 2px;
  }

  &:disabled,
  &[aria-disabled='true'] {
    cursor: not-allowed;
    opacity: 0.5;
  }

  @media (forced-colors: active) {
    &::before {
      @include mixins.high-contrast-outline;
    }
  }
}

.label {
  padding-inline: var(--space-1);
}

// Override default anchor styles
a.base {
  text-decoration: none;

  &:focus {
    border-radius: var(--radius-round);
  }

  &:hover {
    text-decoration: inherit;
  }
}

@mixin active {
  &:active,
  &[aria-pressed='true']:not(.noActiveHighlight),
  &[aria-expanded='true']:not(.noActiveHighlight) {
    @content;
  }
}

// Sizes

.xs {
  min-height: 20px;
}

.sm {
  --inline-padding: var(--space-2);

  padding-block: var(--space-1);
  min-height: 32px;
}

.md {
  --inline-padding: var(--space-3);

  padding-block: var(--space-2);
  min-height: 40px;
}

.lg {
  --inline-padding: var(--space-4);

  padding-block: var(--space-3);
  min-height: 48px;
}

// Solid variant

.solid {
  --bg: var(--color-bg-inverted);
  --fg: var(--color-text-inverted);

  &.contrastOnMedia:not(.accent, .destructive) {
    border: 1px solid var(--color-border-primary);
  }

  &.accent {
    --bg: var(--color-bg-brand-base);
    --fg: var(--color-text-on-brand-base);
  }

  &.destructive {
    --bg: var(--color-bg-error-base);
    --fg: var(--color-text-on-error-base);
  }
}

// Tonal

.tonal {
  --bg-color: var(--color-text-primary);
  --bg-opacity: 4%;
  --bg: rgb(from var(--bg-color) r g b / var(--bg-opacity));
  --bg-hover-opacity: 10%;
  --bg-hover: rgb(from var(--bg-color) r g b / var(--bg-hover-opacity));

  &.neutral {
    @include active {
      --bg: var(--color-bg-inverted);
      --fg: var(--color-text-inverted);
    }
  }

  &.accent,
  &.destructive {
    --bg-opacity: 10%;
    --bg-hover-opacity: 20%;

    @include active {
      --bg-opacity: 100%;
    }
  }

  &.accent {
    --bg-color: var(--color-bg-brand-base);
    --fg: var(--color-text-brand);

    @include active {
      --fg: var(--color-text-on-brand-base);
    }
  }

  &.destructive {
    --bg-color: var(--color-bg-error-base);
    --fg: var(--color-text-error);

    @include active {
      --fg: var(--color-text-on-error-base);
    }
  }
}

// Ghost

.ghost {
  --bg-hover: var(--color-bg-highlight);

  @include active {
    --bg: var(--color-bg-inverted);
    --fg: var(--color-text-inverted);
  }

  &.accent {
    --fg: var(--color-text-brand);

    @include active {
      --bg: var(--color-bg-brand-base);
      --fg: var(--color-text-on-brand-base);
    }
  }

  &.destructive {
    --fg: var(--color-text-error);

    @include active {
      --bg: var(--color-bg-error-base);
      --fg: var(--color-text-on-error-base);
    }
  }
}

// Toggle buttons
.toggle {
  &:hover {
    background-color: var(--bg-hover);
  }

  &.solid {
    @include active {
      --bg: var(--color-bg-brand-base);
      --fg: var(--color-text-on-brand-base);
    }
  }

  &.tonal {
    @include active {
      --bg: rgb(from var(--color-bg-brand-base) r g b / 10%);
      --fg: var(--color-text-brand);
    }
  }

  &.ghost {
    @include active {
      --bg: transparent;
      --fg: var(--color-text-brand);
    }
  }
}

// Helper classes

.icon,
.loading {
  width: var(--fs-20);
  height: var(--fs-20);
}

.iconCustom {
  width: inherit;
  height: inherit;
}

.iconOnly {
  aspect-ratio: 1;
  padding: 0;
}

.loading {
  color: inherit;
}

.clipPadding {
  margin-inline-start: calc(-1 * (var(--inline-padding) + var(--space-1)));

  &:has(:global(.icon-leading)) {
    margin-inline-start: calc(-1 * var(--inline-padding));
  }
}
